import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_WHITE } from "@/lib/assets";

const TIPS = [
  { label: "Lo sapevi?", text: "Il QR del passaporto funziona anche offline, da qualsiasi smartphone." },
  { label: "SOS rapido", text: "Tieni premuta l'icona ORIO per 3 secondi: allerta 80 km intorno a te." },
  { label: "Privacy", text: "I documenti del Cassetto sono cifrati e accessibili solo a te." },
  { label: "Community", text: "4.231 famiglie italiane stanno crescendo con ORIO. Tu sei una di loro." },
  { label: "In Italia oggi", text: "23 cani sono stati aiutati grazie a missioni ORIO nelle ultime 24 ore." },
  { label: "Match leggeri", text: "I match scadono in 24 h: niente conversazioni morte, solo incontri reali." },
  { label: "Diario", text: "Aggiungi voci al diario del tuo cane: passeggiate, peso, foto, disegni." },
  { label: "Installa", text: "Aggiungi ORIO alla schermata Home: pesa zero, funziona offline." },
  { label: "Emergenza", text: "Hai 30 secondi per annullare un'allerta inviata per errore." },
  { label: "Smart", text: "ORIO ti suggerisce match e missioni in base alla tua posizione." },
  { label: "Numero verde", text: "Stiamo costruendo una rete italiana di volontari verificati. Vuoi unirti?" },
  { label: "Trasparenza", text: "Zero pubblicità, zero vendita di dati. Solo 4,99 €/mese." },
];

const DURATION = 4000;

export default function SplashScreen({ onDone }) {
  const [tip] = useState(() => TIPS[Math.floor(Math.random() * TIPS.length)]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const i = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / DURATION);
      setProgress(p);
      if (p >= 1) {
        clearInterval(i);
        setTimeout(onDone, 250);
      }
    }, 50);
    return () => clearInterval(i);
  }, [onDone]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[2000] bg-[#FBF7F1] flex flex-col items-center justify-center px-6 overflow-hidden"
      >
        {/* Color flood that shifts */}
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-40"
            style={{
              background: "radial-gradient(circle, rgba(255,59,48,0.4) 0%, rgba(255,176,32,0.3) 40%, rgba(52,211,153,0.3) 70%, transparent 90%)",
              animation: "orio-bg-shift 4s ease infinite",
              backgroundSize: "200% 200%",
            }} />
        </div>

        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative"
        >
          {/* Concentric ripples — 3 rings emanating from the logo (premium cinematic feel) */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              aria-hidden
              className="absolute top-1/2 left-1/2 rounded-full pointer-events-none"
              style={{
                width: "112px",
                height: "112px",
                marginLeft: "-56px",
                marginTop: "-56px",
                border: "1.5px solid rgba(255,176,32,0.55)",
                boxShadow: "0 0 22px rgba(255,176,32,0.30), inset 0 0 12px rgba(52,211,153,0.18)",
              }}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{
                scale: [0.6, 2.4],
                opacity: [0.75, 0],
                borderColor: [
                  "rgba(255,176,32,0.55)",
                  "rgba(255,122,26,0.35)",
                  "rgba(52,211,153,0.20)",
                ],
              }}
              transition={{
                duration: 2.6,
                delay: i * 0.85,
                repeat: Infinity,
                ease: "easeOut",
              }}
            />
          ))}

          {/* Logo — with subtle breathing pulse over the base entrance */}
          <motion.div
            className="relative h-24 w-24 sm:h-28 sm:w-28"
            animate={{ scale: [1, 1.045, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 orio-grad-bg blur-2xl opacity-50 rounded-full" />
            <div
              className="relative w-full h-full"
              style={{
                background: "linear-gradient(120deg, #ff3b30 0%, #ffb020 50%, #34d399 100%)",
                backgroundSize: "200% 200%",
                animation: "orio-bg-shift 2.5s ease infinite",
                WebkitMask: `url(${LOGO_WHITE}) no-repeat center / contain`,
                mask: `url(${LOGO_WHITE}) no-repeat center / contain`,
              }}
            />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-10 max-w-sm text-center relative z-10"
        >
          <div className="text-[10px] uppercase tracking-[0.28em]"
            style={{
              background: "linear-gradient(120deg, #ff3b30, #ffb020, #34d399)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}>
            {tip.label}
          </div>
          <p className="mt-3 text-sm sm:text-base text-[#3D3D3D] leading-relaxed">
            {tip.text}
          </p>
        </motion.div>

        {/* Progress bar */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 w-32 h-[2px] bg-[#EAE3D2] rounded-full overflow-hidden">
          <div className="h-full orio-grad-bg transition-[width] duration-100" style={{ width: `${progress * 100}%` }} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
